import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Plus, Check, X, Pencil } from "lucide-react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { OpsSubnav } from "@/components/OpsSubnav";

export const Route = createFileRoute("/admin/ops/renata")({
  component: AdminRenataPage,
  head: () => ({ meta: [{ title: "Renata — Admin — Structura" }] }),
});

// ── Types ────────────────────────────────────────────────────────────────────

interface TenantUsage {
  tenant_id: string;
  total_queries: number;
  total_cost_microcents: number;
  total_tokens: number;
  errors: number;
}

interface PackRow {
  id: string;
  name: string;
  description: string | null;
  quantity: number;
  price_ars: number;
  price_usd: number | null;
  validity_days: number;
  active: boolean;
  featured: boolean;
  recommended: boolean;
  sort_order: number;
}

// ── Usage Section ─────────────────────────────────────────────────────────────

function UsageSection() {
  const [usage, setUsage] = useState<TenantUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void apiGetJson<{ usage: TenantUsage[] }>("/api/renata/usage?global=1&days=30")
      .then((d) => setUsage(d.usage ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const stats = usage.reduce(
    (acc, u) => ({
      tenants: acc.tenants + 1,
      queries: acc.queries + Number(u.total_queries),
      cost: acc.cost + Number(u.total_cost_microcents),
      tokens: acc.tokens + Number(u.total_tokens),
      errors: acc.errors + Number(u.errors),
    }),
    { tenants: 0, queries: 0, cost: 0, tokens: 0, errors: 0 },
  );

  function mc(microcents: number) {
    return `$${(microcents / 100_000_000).toFixed(4)}`;
  }

  if (loading) return <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: "Tenants activos", value: String(stats.tenants) },
          { label: "Consultas totales", value: stats.queries.toLocaleString("es-AR") },
          { label: "Costo total (USD)", value: mc(stats.cost) },
          { label: "Errores", value: String(stats.errors) },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border bg-card p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Tenant</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Consultas</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Créditos</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Costo</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Errores</th>
            </tr>
          </thead>
          <tbody>
            {usage.map((row) => (
              <tr key={row.tenant_id} className="border-b last:border-0 hover:bg-muted/20">
                <td className="px-4 py-2 font-mono text-xs">{row.tenant_id}</td>
                <td className="px-4 py-2 text-right">{Number(row.total_queries).toLocaleString("es-AR")}</td>
                <td className="px-4 py-2 text-right">{Number(row.total_tokens).toLocaleString("es-AR")}</td>
                <td className="px-4 py-2 text-right">{mc(Number(row.total_cost_microcents))}</td>
                <td className="px-4 py-2 text-right">
                  <span className={Number(row.errors) > 0 ? "text-red-600" : "text-muted-foreground"}>{Number(row.errors)}</span>
                </td>
              </tr>
            ))}
            {usage.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin datos en este período</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Packs Section ─────────────────────────────────────────────────────────────

function PacksSection() {
  const [packs, setPacks] = useState<PackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<PackRow>>({});
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPack, setNewPack] = useState({ name: "", quantity: "", priceArs: "", validityDays: "90" });
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    void apiGetJson<{ packs: PackRow[] }>("/api/admin/renata/packs")
      .then((d) => setPacks(d.packs ?? []))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function startEdit(p: PackRow) {
    setEditing(p.id);
    setDraft({ ...p });
  }

  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      await apiSendJson("/api/admin/renata/packs", "PATCH", {
        id: editing,
        name: draft.name,
        quantity: draft.quantity,
        priceArs: draft.price_ars,
        priceUsd: draft.price_usd,
        validityDays: draft.validity_days,
        active: draft.active,
        featured: draft.featured,
        recommended: draft.recommended,
        sortOrder: draft.sort_order,
      });
      setEditing(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function create() {
    if (!newPack.name || !newPack.quantity || !newPack.priceArs) return;
    setSaving(true);
    try {
      await apiSendJson("/api/admin/renata/packs", "POST", {
        name: newPack.name,
        quantity: Number(newPack.quantity),
        priceArs: Number(newPack.priceArs),
        validityDays: Number(newPack.validityDays),
      });
      setCreating(false);
      setNewPack({ name: "", quantity: "", priceArs: "", validityDays: "90" });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al crear");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="flex h-16 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3">
      {error && <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-400">{error}</p>}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Nombre</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Créditos</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">ARS</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Días</th>
              <th className="px-4 py-2 text-center font-medium text-muted-foreground">Activo</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {packs.map((p) => (
              <tr key={p.id} className="border-b last:border-0 hover:bg-muted/20">
                {editing === p.id ? (
                  <>
                    <td className="px-4 py-2"><input className="w-full rounded border bg-background px-2 py-1 text-sm" value={draft.name ?? ""} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} /></td>
                    <td className="px-4 py-2"><input className="w-20 rounded border bg-background px-2 py-1 text-right text-sm" value={draft.quantity ?? ""} type="number" onChange={(e) => setDraft((d) => ({ ...d, quantity: Number(e.target.value) }))} /></td>
                    <td className="px-4 py-2"><input className="w-24 rounded border bg-background px-2 py-1 text-right text-sm" value={draft.price_ars ?? ""} type="number" onChange={(e) => setDraft((d) => ({ ...d, price_ars: Number(e.target.value) }))} /></td>
                    <td className="px-4 py-2"><input className="w-16 rounded border bg-background px-2 py-1 text-right text-sm" value={draft.validity_days ?? ""} type="number" onChange={(e) => setDraft((d) => ({ ...d, validity_days: Number(e.target.value) }))} /></td>
                    <td className="px-4 py-2 text-center"><input type="checkbox" checked={draft.active ?? false} onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))} /></td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button disabled={saving} onClick={() => void save()} className="rounded p-1 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900">
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button onClick={() => setEditing(null)} className="rounded p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2 font-medium">{p.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{Number(p.quantity).toLocaleString("es-AR")}</td>
                    <td className="px-4 py-2 text-right tabular-nums">${Number(p.price_ars).toLocaleString("es-AR")}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{p.validity_days}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-block h-2 w-2 rounded-full ${p.active ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => startEdit(p)} className="rounded p-1 text-muted-foreground hover:bg-muted"><Pencil className="h-3.5 w-3.5" /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {packs.length === 0 && !creating && (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin packs AI configurados</td></tr>
            )}
            {creating && (
              <tr className="border-b bg-muted/10">
                <td className="px-4 py-2"><input className="w-full rounded border bg-background px-2 py-1 text-sm" placeholder="Nombre del pack" value={newPack.name} onChange={(e) => setNewPack((d) => ({ ...d, name: e.target.value }))} /></td>
                <td className="px-4 py-2"><input className="w-20 rounded border bg-background px-2 py-1 text-right text-sm" placeholder="500" type="number" value={newPack.quantity} onChange={(e) => setNewPack((d) => ({ ...d, quantity: e.target.value }))} /></td>
                <td className="px-4 py-2"><input className="w-24 rounded border bg-background px-2 py-1 text-right text-sm" placeholder="4999" type="number" value={newPack.priceArs} onChange={(e) => setNewPack((d) => ({ ...d, priceArs: e.target.value }))} /></td>
                <td className="px-4 py-2"><input className="w-16 rounded border bg-background px-2 py-1 text-right text-sm" type="number" value={newPack.validityDays} onChange={(e) => setNewPack((d) => ({ ...d, validityDays: e.target.value }))} /></td>
                <td className="px-4 py-2" />
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <button disabled={saving} onClick={() => void create()} className="rounded p-1 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </button>
                    <button onClick={() => setCreating(false)} className="rounded p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!creating && (
        <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground">
          <Plus className="h-4 w-4" /> Nuevo pack AI
        </button>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type Tab = "usage" | "packs";

function AdminRenataPage() {
  const [tab, setTab] = useState<Tab>("usage");

  const tabs: { id: Tab; label: string }[] = [
    { id: "usage", label: "Consumo por tenant" },
    { id: "packs", label: "Catálogo de packs AI" },
  ];

  return (
    <div className="p-6 space-y-6">
      <OpsSubnav hint="Consumo de IA por tenant y catálogo de packs — costo/uso real, no estimado." />
      <div>
        <h1 className="text-lg font-semibold">Renata — Panel Owner</h1>
        <p className="text-sm text-muted-foreground">
          Últimos 30 días · Créditos por plan: <a href="/admin?tab=catalogo" className="text-primary underline-offset-2 hover:underline">Catálogo de Planes → Límites & cuotas</a>
        </p>
      </div>

      <div className="flex gap-1 border-b">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === "usage" && <UsageSection />}
        {tab === "packs" && <PacksSection />}
      </div>
    </div>
  );
}
